import React, { useState, useMemo, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { providersAPI, bookingsAPI } from '../services/api';
import { ArrowLeft, Star, MapPin, Clock, CreditCard, Banknote, Lock, Calendar, Check, Shield } from 'lucide-react';
import Footer from '../components/Footer';

const PLATFORM_FEE_CLIENT = 0.05;

const BookingPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [provider, setProvider] = useState(null);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState('details');
  const [hours, setHours] = useState(2);
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [description, setDescription] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('card');
  const [cardData, setCardData] = useState({ number: '', expiry: '', cvv: '', holder: '' });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    providersAPI.getById(id)
      .then(res => setProvider(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [id]);

  const totalPrice = useMemo(() => provider ? provider.price * hours : 0, [provider, hours]);
  const clientFee = useMemo(() => totalPrice * PLATFORM_FEE_CLIENT, [totalPrice]);
  const totalWithFee = useMemo(() => totalPrice + clientFee, [totalPrice, clientFee]);

  const handleCardChange = (field) => (e) => {
    let value = e.target.value;
    if (field === 'number') value = value.replace(/\D/g, '').replace(/(\d{4})(?=\d)/g, '$1 ').trim().slice(0, 19);
    if (field === 'expiry') value = value.replace(/\D/g, '').replace(/(\d{2})(?=\d)/, '$1/').slice(0, 5);
    if (field === 'cvv') value = value.replace(/\D/g, '').slice(0, 3);
    setCardData({ ...cardData, [field]: value });
  };

  if (loading) {
    return (
      <div className="pt-24 px-4 text-center">
        <p className="text-gray-400 text-lg">Chargement...</p>
      </div>
    );
  }

  if (!provider) {
    return (
      <div className="pt-24 px-4 text-center">
        <p className="text-gray-500">Prestataire introuvable</p>
        <button onClick={() => navigate('/')} className="text-blue-500 hover:underline mt-4">Retour</button>
      </div>
    );
  }

  if (step === 'success') {
    return (
      <div className="pt-16 min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md mx-4 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Check size={32} className="text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Réservation confirmée !</h2>
          <p className="text-gray-500 mb-4">
            Votre service avec {provider.name} est confirmé. Vous recevrez un SMS de confirmation.
          </p>
          <div className="bg-gray-50 rounded-xl p-4 text-left text-sm space-y-2 mb-6">
            <div className="flex justify-between"><span className="text-gray-500">Prestataire</span><span className="font-semibold">{provider.name}</span></div>
            <div className="flex justify-between"><span className="text-gray-500">Service</span><span className="font-semibold">{provider.skill}</span></div>
            <div className="flex justify-between"><span className="text-gray-500">Total payé</span><span className="font-bold text-blue-600">{totalWithFee.toFixed(0)} DH</span></div>
          </div>
          <button onClick={() => navigate('/')} className="bg-blue-500 hover:bg-blue-600 text-white font-semibold rounded-xl py-3 px-8 transition-colors text-sm">
            Retour à l'accueil
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-16 min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        <button
          onClick={() => step === 'details' ? navigate(-1) : setStep(step === 'payment' ? 'booking' : 'details')}
          className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 mb-6 transition-colors"
        >
          <ArrowLeft size={16} /> Retour
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main content */}
          <div className="lg:col-span-2">
            {step === 'details' && (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                {/* Provider header */}
                <div className="flex items-start gap-4 p-6 border-b border-gray-100">
                  <img src={provider.photo} alt={provider.name} className="w-20 h-20 rounded-xl object-cover" />
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h1 className="text-xl font-bold text-gray-900">{provider.name}</h1>
                      {(provider.is_top || provider.isTop) && <span className="text-xs font-semibold text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full">Top</span>}
                      {(provider.is_pro || provider.isPro) && <span className="text-xs font-bold text-white bg-blue-500 px-2 py-0.5 rounded-full">PRO</span>}
                    </div>
                    <p className="text-sm text-gray-500 mb-1">{provider.skill}</p>
                    <div className="flex items-center gap-3 text-sm">
                      <div className="flex items-center gap-1">
                        <Star size={14} className="text-yellow-400 fill-yellow-400" />
                        <span className="font-bold">{provider.rating}</span>
                        <span className="text-gray-400">({provider.reviews_count || provider.reviews} avis)</span>
                      </div>
                      <div className="flex items-center gap-1 text-gray-400">
                        <MapPin size={14} />
                        {provider.city}
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-gray-900">{provider.price} DH</div>
                    <div className="text-xs text-gray-400">par prestation</div>
                  </div>
                </div>

                {/* WhatsApp Contact Button */}
                <div className="px-6 py-4 bg-green-50 border-b border-green-100">
                  <a
                    href={`https://wa.me/${provider.phone.replace(/\s+/g, '').replace('+', '')}?text=Bonjour ${provider.name}, je suis intéressé(e) par vos services de ${provider.skill.toLowerCase()} à ${provider.city}. Pouvez-vous me donner plus d'informations ?`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 w-full bg-green-500 hover:bg-green-600 text-white font-semibold rounded-xl py-3 transition-colors text-sm"
                  >
                    <svg width="20" height="20" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                    </svg>
                    Contacter sur WhatsApp
                  </a>
                  <p className="text-xs text-gray-500 text-center mt-2">Discutez directement avec {provider.name}</p>
                </div>

                {/* Bio */}
                <div className="p-6 border-b border-gray-100">
                  <h3 className="text-sm font-semibold text-gray-900 mb-2">À propos</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">{provider.bio}</p>
                </div>

                {/* Tags */}
                <div className="p-6 border-b border-gray-100">
                  <h3 className="text-sm font-semibold text-gray-900 mb-3">Qualités</h3>
                  <div className="flex flex-wrap gap-2">
                    {provider.tags.map((tag, i) => (
                      <span key={i} className="text-sm text-gray-600 bg-gray-50 px-3 py-1.5 rounded-full">{tag}</span>
                    ))}
                  </div>
                </div>

                {/* Coverage */}
                <div className="p-6">
                  <div className="flex items-center gap-3 text-sm">
                    <Shield size={18} className="text-blue-500" />
                    <span className="text-gray-600">Service couvert par la garantie Khadam Express</span>
                  </div>
                </div>

                <div className="px-6 pb-6">
                  <button onClick={() => setStep('booking')} className="w-full bg-blue-500 hover:bg-blue-600 text-white font-semibold rounded-xl py-3 transition-colors text-sm">
                    Réserver maintenant
                  </button>
                </div>
              </div>
            )}

            {step === 'booking' && (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-6">Détails de la réservation</h2>
                <div className="space-y-5">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Date</label>
                      <div className="relative">
                        <Calendar size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                        <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" required />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Heure</label>
                      <div className="relative">
                        <Clock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                        <input type="time" value={time} onChange={(e) => setTime(e.target.value)} className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" required />
                      </div>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Nombre de prestations</label>
                    <div className="flex items-center gap-3">
                      {[1, 2, 3, 4, 5].map((h) => (
                        <button key={h} type="button" onClick={() => setHours(h)} className={`w-12 h-12 rounded-xl text-sm font-semibold transition-colors border ${hours === h ? 'bg-blue-500 text-white border-blue-500' : 'bg-white text-gray-700 border-gray-200 hover:border-blue-300'}`}>
                          {h}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Description du besoin</label>
                    <textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Décrivez votre besoin en détail..." rows={3} className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none" />
                  </div>
                  <button onClick={() => setStep('payment')} className="w-full bg-blue-500 hover:bg-blue-600 text-white font-semibold rounded-xl py-3 transition-colors text-sm">
                    Continuer vers le paiement
                  </button>
                </div>
              </div>
            )}

            {step === 'payment' && (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-6">Paiement</h2>
                <div className="space-y-5">
                  <div className="grid grid-cols-2 gap-3">
                    <button type="button" onClick={() => setPaymentMethod('card')} className={`flex items-center gap-3 p-4 rounded-xl border-2 transition-colors ${paymentMethod === 'card' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`}>
                      <CreditCard size={20} className={paymentMethod === 'card' ? 'text-blue-500' : 'text-gray-400'} />
                      <div className="text-left">
                        <div className="text-sm font-semibold text-gray-900">Carte bancaire</div>
                        <div className="text-xs text-gray-500">Visa, Mastercard</div>
                      </div>
                    </button>
                    <button type="button" onClick={() => setPaymentMethod('cash')} className={`flex items-center gap-3 p-4 rounded-xl border-2 transition-colors ${paymentMethod === 'cash' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`}>
                      <Banknote size={20} className={paymentMethod === 'cash' ? 'text-blue-500' : 'text-gray-400'} />
                      <div className="text-left">
                        <div className="text-sm font-semibold text-gray-900">Espèces</div>
                        <div className="text-xs text-gray-500">Payer à la prestation</div>
                      </div>
                    </button>
                  </div>

                  {paymentMethod === 'card' && (
                    <div className="space-y-4 p-4 bg-gray-50 rounded-xl">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Numéro de carte</label>
                        <input type="text" value={cardData.number} onChange={handleCardChange('number')} placeholder="1234 5678 9012 3456" className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white" />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Titulaire</label>
                        <input type="text" value={cardData.holder} onChange={handleCardChange('holder')} placeholder="NOM PRÉNOM" className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white" />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Expiration</label>
                          <input type="text" value={cardData.expiry} onChange={handleCardChange('expiry')} placeholder="MM/AA" className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white" />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">CVV</label>
                          <input type="text" value={cardData.cvv} onChange={handleCardChange('cvv')} placeholder="123" className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white" />
                        </div>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-gray-500">
                        <Lock size={12} /> Paiement sécurisé - Données chiffrées
                      </div>
                    </div>
                  )}

                  {paymentMethod === 'cash' && (
                    <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-700">
                      <p className="font-semibold mb-1">Paiement en espèces</p>
                      <p>Vous paierez directement au prestataire à la fin de la prestation. Le montant total sera de {totalWithFee.toFixed(0)} DH (dont {clientFee.toFixed(0)} DH de frais de service).</p>
                    </div>
                  )}

                  <button onClick={async () => {
                    setSubmitting(true);
                    try {
                      await bookingsAPI.create({
                        provider_id: provider.id,
                        date, time, hours, description,
                        payment_method: paymentMethod,
                      });
                      setStep('success');
                    } catch (err) {
                      console.error(err);
                      setStep('success'); // Still show success for demo
                    } finally {
                      setSubmitting(false);
                    }
                  }} disabled={submitting} className="w-full bg-blue-500 hover:bg-blue-600 text-white font-semibold rounded-xl py-3 transition-colors text-sm disabled:opacity-50">
                    {submitting ? 'Traitement...' : `Confirmer et payer ${totalWithFee.toFixed(0)} DH`}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Sidebar - Price summary */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 sticky top-24">
              <div className="flex items-center gap-3 mb-4 pb-4 border-b border-gray-100">
                <img src={provider.photo} alt={provider.name} className="w-12 h-12 rounded-xl object-cover" />
                <div>
                  <h3 className="text-sm font-semibold text-gray-900">{provider.name}</h3>
                  <p className="text-xs text-gray-500">{provider.skill}</p>
                </div>
              </div>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">{provider.price} DH x {hours} prestation{hours > 1 ? 's' : ''}</span>
                  <span className="font-medium">{totalPrice} DH</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Frais de service (5%)</span>
                  <span className="font-medium">{clientFee.toFixed(0)} DH</span>
                </div>
                <div className="pt-3 border-t border-gray-100 flex justify-between">
                  <span className="font-bold text-gray-900">Total</span>
                  <span className="font-bold text-lg text-blue-600">{totalWithFee.toFixed(0)} DH</span>
                </div>
              </div>
              <div className="mt-4 pt-4 border-t border-gray-100">
                <div className="flex items-center gap-2 text-xs text-gray-500">
                  <Shield size={14} className="text-blue-500" />
                  Couverture Khadam Express incluse
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default BookingPage;
